import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { getEventById as getEventByIdStatic } from '../data/eventData';
import { getEventById, deleteEvent } from '../services/firestoreService';
import {
  generateGoogleCalendarUrl,
  generateOutlookCalendarUrl,
  downloadICalendarFile
} from '../utils/calendarUtils';
import EventRegistration from '../components/events/EventRegistration';
import AttendeeList from '../components/events/AttendeeList';
import EventDiscountManager from '../components/admin/EventDiscountManager';

import ConnectedAttendees from '../components/events/ConnectedAttendees';
import { useAuth } from '../contexts/AuthContext';
import { exportAttendeesToExcel } from '../services/attendeeService';
import { validateDiscountCode } from '../services/discountService';
// import { createEventCheckoutSession } from '../services/eventPaymentService';

const EventDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { currentUser, hasRole, userProfile } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showAttendees, setShowAttendees] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountValid, setDiscountValid] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [showDiscountManager, setShowDiscountManager] = useState(false);

  // Check if current user is admin and logged in
  const isAdmin = hasRole('admin');
  const isUserLoggedIn = !!currentUser;

  // Temporary debug info for admin troubleshooting
  console.log('🔍 Admin Debug Info:', {
    currentUser: currentUser?.email,
    userProfile: userProfile,
    isAdmin: isAdmin,
    hasRoleFunction: typeof hasRole
  });

  // Function to get price range for display (Member vs Non-Member only)
  const getPriceRange = (event) => {
    // Handle new pricing structure - EXCLUDE sponsor pricing
    if (event.pricing) {
      const prices = [];
      if (event.pricing.member !== undefined) prices.push(event.pricing.member);
      if (event.pricing.nonMember !== undefined) prices.push(event.pricing.nonMember);
      // NOTE: Intentionally excluding sponsor pricing - sponsors use discount codes

      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        if (minPrice === maxPrice) {
          return `$${minPrice.toFixed(2)}`;
        } else {
          return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
        }
      }
    }

    // Handle legacy ticketTypes structure - EXCLUDE sponsor tickets
    if (event.ticketTypes && Array.isArray(event.ticketTypes)) {
      const prices = event.ticketTypes
        .filter(ticket => ticket.id !== 'sponsor' && !ticket.requiresSponsorSelection) // Exclude sponsor tickets
        .map(ticket => ticket.price)
        .filter(price => price !== undefined);

      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        if (minPrice === maxPrice) {
          return `$${minPrice.toFixed(2)}`;
        } else {
          return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
        }
      }
    }

    // Handle static data price field (like "$125.00 - $160.00")
    if (event.price && typeof event.price === 'string') {
      // If it already contains a range, return as-is
      if (event.price.includes(' - ') || event.price.includes('-')) {
        return event.price;
      }
      // If it's a single price, return as-is
      return event.price;
    }

    // Fallback
    return 'Contact for pricing';
  };

  // Validate discount codes using centralized service
  const validateEventDiscountCode = async (code) => {
    const upperCode = code.toUpperCase();

    // Check event-specific discount codes first
    if (event?.discountCodes) {
      const eventCode = event.discountCodes.find(dc =>
        dc.code === upperCode &&
        dc.active &&
        (!dc.validUntil || new Date(dc.validUntil) >= new Date()) &&
        (!dc.maxUses || (dc.usedCount || 0) < dc.maxUses)
      );

      if (eventCode) {
        return {
          valid: true,
          discount: eventCode.discount,
          type: eventCode.type || 'percentage'
        };
      }
    }

    // Use centralized discount service for global event codes
    try {
      const validation = await validateDiscountCode(code, "event");
      if (validation.valid) {
        return {
          valid: true,
          discount: validation.discountAmount,
          type: validation.discountType
        };
      }
    } catch (error) {
      console.error('Error validating discount code:', error);
    }

    // Fallback to hardcoded admin codes for backward compatibility
    if (upperCode === 'ADMIN50' || upperCode === 'APNADMIN') {
      return { valid: true, discount: 50, type: 'percentage' };
    }

    return { valid: false, discount: 0, type: null };
  };

  // Handle discount code input
  const handleDiscountCode = async (code) => {
    setDiscountCode(code);
    if (code.trim()) {
      const validation = await validateEventDiscountCode(code);
      setDiscountValid(validation.valid);
      setDiscountAmount(validation.discount);
    } else {
      setDiscountValid(false);
      setDiscountAmount(0);
    }
  };

  // Calculate discounted price
  const calculateDiscountedPrice = (originalPrice) => {
    if (discountValid && discountAmount > 0) {
      return originalPrice * (1 - discountAmount / 100);
    }
    return originalPrice;
  };

  // Handle Stripe checkout for event registration - UPDATED VERSION 2025-08-20
  const handleStripeCheckout = async (registrationType) => {
    console.log('🚀 FUNCTION CALLED - handleStripeCheckout with type:', registrationType);
    console.log('🚀 NEW VERSION LOADED - This should show the updated code is running');
    console.log('🔍 STRIPE CHECKOUT START - Registration type:', registrationType);
    console.log('🔍 STRIPE CHECKOUT START - Current user:', currentUser);
    console.log('🔍 STRIPE CHECKOUT START - Event:', event);

    // Only require login for member and sponsor pricing
    if (!currentUser && (registrationType === 'member' || registrationType === 'sponsor')) {
      alert('Please log in to access member or sponsor pricing.');
      return;
    }

    try {
      setPaymentLoading(true);

      // Calculate price based on registration type and discount
      let basePrice = 0;
      if (registrationType === 'member') {
        basePrice = event.pricing?.memberPrice || event.memberPrice || 0;
      } else if (registrationType === 'nonMember') {
        basePrice = event.pricing?.nonMemberPrice || event.nonMemberPrice || 0;
      } else if (registrationType === 'sponsor') {
        basePrice = event.pricing?.sponsorPrice || event.sponsorPrice || 0;
      }

      // Apply discount if valid
      let finalPrice = basePrice;
      if (discountCode && discountValid) {
        finalPrice = basePrice * (1 - discountAmount / 100);
      }

      // Event data for checkout (used in console logs below)

      console.log('🔍 CHECKOUT DEBUG - Event object:', event);
      console.log('🔍 CHECKOUT DEBUG - Event has ticketTypes:', !!event?.ticketTypes);
      console.log('🔍 CHECKOUT DEBUG - Event ticketTypes:', event?.ticketTypes);
      console.log('🔍 CHECKOUT DEBUG - Registration type:', registrationType);
      console.log('🔍 CHECKOUT DEBUG - Final price:', finalPrice);

      // Create Stripe checkout session
      const response = await fetch('https://us-central1-the-apn.cloudfunctions.net/createEventCheckoutSession', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify((() => {
          // Get the price for the selected registration type
          let eventPrice = 0;
          if (registrationType === 'member' && event.pricing?.member !== undefined) {
            eventPrice = discountValid ? calculateDiscountedPrice(event.pricing.member) : event.pricing.member;
          } else if (registrationType === 'nonMember' && event.pricing?.nonMember !== undefined) {
            eventPrice = discountValid ? calculateDiscountedPrice(event.pricing.nonMember) : event.pricing.nonMember;
          } else if (registrationType === 'sponsor' && event.pricing?.sponsor !== undefined) {
            eventPrice = discountValid ? calculateDiscountedPrice(event.pricing.sponsor) : event.pricing.sponsor;
          }

          const requestBody = {
            eventId: event.id,
            userId: currentUser?.uid || null,
            registrationType,
            discountCode: discountCode || null,
            eventTitle: event.title,
            eventPrice: eventPrice,
            successUrl: `${window.location.origin}/events/${event.id}/registration/success`,
            cancelUrl: `${window.location.origin}/events/${event.id}`
          };
          console.log('🔍 CHECKOUT DEBUG - Request body:', JSON.stringify(requestBody, null, 2));
          return requestBody;
        })()),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const session = await response.json();

      // Redirect to Stripe checkout
      const stripe = await import('@stripe/stripe-js').then(m => m.loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY));
      const { error } = await stripe.redirectToCheckout({
        sessionId: session.id
      });

      if (error) {
        console.error('Stripe checkout error:', error);
        alert('Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Unable to process payment. Please try again or contact support.');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Utility function to check if event has passed
  const isEventPast = (eventData) => {
    if (!eventData) return false;

    const now = new Date();
    let eventDate;

    // Handle different date formats
    if (eventData.startDate) {
      // Firebase format with startDate and startTime
      if (eventData.startTime) {
        eventDate = new Date(`${eventData.startDate}T${eventData.startTime}`);
      } else {
        eventDate = new Date(eventData.startDate);
      }
    } else if (eventData.date) {
      // Static data format with date field
      eventDate = new Date(eventData.date);
    } else if (eventData.startDateTime) {
      // Alternative format
      eventDate = new Date(eventData.startDateTime);
    }

    return eventDate && eventDate < now;
  };

  // Handle Excel export for admins
  const handleExportAttendees = async (includePaymentInfo = false) => {
    if (!isAdmin) {
      alert('Only administrators can export attendee lists.');
      return;
    }

    try {
      const result = await exportAttendeesToExcel(event.id, includePaymentInfo);
      if (result.success) {
        alert(`Successfully exported ${result.attendeeCount} attendees to ${result.filename}`);
      }
    } catch (error) {
      console.error('Error exporting attendees:', error);
      alert('Failed to export attendee list. Please try again.');
    }
  };

  // Handle event deletion
  const handleDeleteEvent = async () => {
    if (!event) return;

    const confirmMessage = `Are you sure you want to delete the event "${event.title}"?\n\nThis action will:\n- Delete the event permanently\n- Cancel all registrations\n- Remove all attendee data\n\nThis cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      try {
        console.log('🗑️ Attempting to delete event with ID:', event.id, 'Type:', typeof event.id);

        // Try different ID formats
        let eventIdToDelete = event.id;

        // If it's a Firebase document, use the document ID
        if (event.id && typeof event.id === 'string') {
          eventIdToDelete = event.id;
        } else if (event.id && typeof event.id === 'number') {
          // For static data events, we can't delete them from Firebase
          alert(`❌ Cannot Delete Static Event

This event (ID: ${event.id}) is a static demo event that cannot be deleted.

Static events are:
• Pre-loaded demo data from eventData.js
• Used for testing and display purposes
• Cannot be modified or deleted from the database

To work with deletable events:
1. Create new events through Admin Dashboard
2. Use the "Create Grand ReLaunch" admin tool (/admin/create-grand-relaunch)
3. Firebase-generated events (with long string IDs) can be deleted

Current event: "${event.title}" (Static Demo Event)`);
          return;
        }

        console.log('🗑️ Deleting event with ID:', eventIdToDelete);
        await deleteEvent(eventIdToDelete);

        alert('Event deleted successfully');
        // Redirect to events page
        window.location.href = '/events';
      } catch (error) {
        console.error('Error deleting event:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);

        let errorMessage = '';

        if (error.code === 'permission-denied') {
          errorMessage = `❌ Permission Denied

You don't have the required permissions to delete this event.

Possible solutions:
• Make sure you're logged in as an admin
• Check your user role in the database
• Contact a site administrator

Event: "${event.title}"
Error: ${error.message || 'Permission denied'}`;
        } else if (error.code === 'not-found') {
          errorMessage = `❌ Event Not Found

This event may have already been deleted or doesn't exist in the database.

Event ID: ${event.id}
Event Title: "${event.title}"

This might be a static event that cannot be deleted.`;
        } else {
          errorMessage = `❌ Delete Failed

Failed to delete event: "${event.title}"

Error: ${error.message || error.code || 'Unknown error'}

If this persists:
1. Try refreshing the page
2. Check your internet connection
3. Contact support if the issue continues

Event ID: ${event.id}`;
        }

        alert(errorMessage);
      }
    }
  };

  // Load event data function
  const loadEvent = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading event with ID:', id, 'Type:', typeof id);

      // Try Firebase first
      let eventData = await getEventById(id);
      console.log('🔍 Firebase result:', eventData);

      // If not found in Firebase, try static data
      if (!eventData) {
        console.log('Event not found in Firebase, trying static data...');
        eventData = getEventByIdStatic(parseInt(id));
        console.log('🔍 Static data result:', eventData);
      }

      if (!eventData) {
        console.error('Event not found in Firebase or static data');
        setEvent(null);
        return;
      }

      console.log('🔍 Event Detail Debug - Loaded event data:', eventData);
      console.log('🔍 Event Detail Debug - Has ticketTypes:', eventData?.ticketTypes);
      console.log('🔍 Event Detail Debug - Has pricing:', eventData?.pricing);

      // Convert ticketTypes to pricing structure if needed
      if (eventData && eventData.ticketTypes && !eventData.pricing) {
        const pricing = {};
        eventData.ticketTypes.forEach(ticket => {
          if (ticket.id === 'member') pricing.member = ticket.price;
          if (ticket.id === 'non-member') pricing.nonMember = ticket.price;
          if (ticket.id === 'sponsor') pricing.sponsor = ticket.price;
        });
        eventData.pricing = pricing;
        console.log('🔍 Converted ticketTypes to pricing:', pricing);
      }

      // Ensure event has required fields for registration
      if (eventData && !eventData.ticketTypes && eventData.pricing) {
        // Convert pricing back to ticketTypes for compatibility
        const ticketTypes = [];
        if (eventData.pricing.member !== undefined) {
          ticketTypes.push({
            id: 'member',
            name: 'Member Registration',
            price: eventData.pricing.member,
            description: 'For current APN members',
            memberOnly: true
          });
        }
        if (eventData.pricing.nonMember !== undefined) {
          ticketTypes.push({
            id: 'non-member',
            name: 'Non-Member Registration',
            price: eventData.pricing.nonMember,
            description: 'For non-members',
            memberOnly: false
          });
        }
        if (eventData.pricing.sponsor !== undefined) {
          ticketTypes.push({
            id: 'sponsor',
            name: 'Sponsor Registration',
            price: eventData.pricing.sponsor,
            description: 'For event sponsors',
            memberOnly: false
          });
        }
        eventData.ticketTypes = ticketTypes;
        console.log('🔍 Converted pricing to ticketTypes:', ticketTypes);
      }

      setEvent(eventData);
    } catch (error) {
      console.error('Error loading event:', error);
      // Fallback to static data
      try {
        const eventData = getEventByIdStatic(parseInt(id));
        if (eventData) {
          console.log('🔍 Fallback to static data successful:', eventData);
          setEvent(eventData);
        } else {
          console.error('Static data fallback also failed');
          setEvent(null);
        }
      } catch (staticError) {
        console.error('Static data fallback error:', staticError);
        setEvent(null);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch event data
  useEffect(() => {
    loadEvent();
  }, [id, loadEvent]);

  // Clear URL parameters from payment redirects to prevent reprocessing
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true' || canceled === 'true') {
      // Clear the URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      // Show appropriate message
      if (success === 'true') {
        console.log('Payment successful - registration confirmed');
      } else if (canceled === 'true') {
        console.log('Payment canceled by user');
      }
    }
  }, [searchParams]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Event Not Found</h1>
          <p className="text-gray-600 mb-8">The event you're looking for doesn't exist or has been removed.</p>
          <Link to="/events" className="inline-block bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200">
            Back to Events
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.png`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">{event.title}</h1>
          </div>
        </div>
      </div>

      {/* Event Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Event Header */}
            <div className="relative h-96 bg-white">
              {/* Event image */}
              <div className="h-full w-full overflow-hidden flex items-center justify-center bg-white">
                <img
                  src={(() => {
                    // Handle different image URL formats
                    const imageUrl = event.imageUrl || event.image;
                    if (!imageUrl) {
                      return `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                    }
                    // If it's a full URL (Firebase Storage), use it directly
                    if (imageUrl.startsWith('http')) {
                      return imageUrl;
                    }
                    // If it's a relative path, add PUBLIC_URL
                    return `${process.env.PUBLIC_URL}${imageUrl}`;
                  })()}
                  alt={event.title}
                  className="h-full w-full object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`;
                  }}
                />
              </div>
              {event.featured && (
                <div className="absolute top-4 left-4 bg-apn-blue text-white px-3 py-1 rounded-md">
                  Featured
                </div>
              )}
            </div>

            {/* Event Details */}
            <div className="p-6">
              <div className="flex flex-col md:flex-row justify-between mb-8">
                <div className="mb-4 md:mb-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{event.title}</h2>
                  <p className="text-gray-600">{event.description}</p>
                </div>
                <div className="flex flex-col space-y-2">
                  {/* Registration Logic - Check if event has passed */}
                  {!isEventPast(event) && (event.ticketTypes || event.pricing) ? (
                    // Event is upcoming and has ticket types/pricing - show registration button
                    <button
                      onClick={() => setShowRegistration(!showRegistration)}
                      className="bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200"
                    >
                      {showRegistration ? 'Hide Registration' : 'Register Now'}
                    </button>
                  ) : isEventPast(event) ? (
                    // Event has passed - show disabled button
                    <button
                      className="bg-gray-400 text-white py-2 px-6 rounded cursor-not-allowed"
                      disabled
                    >
                      Registration Closed - Event Has Passed
                    </button>
                  ) : (event.ticketTypes || event.pricing) ? (
                    // Event has ticket types/pricing but registration deadline may have passed
                    <button
                      className="bg-gray-400 text-white py-2 px-6 rounded cursor-not-allowed"
                      disabled
                    >
                      Registration Closed
                    </button>
                  ) : null}
                  {isUserLoggedIn && (
                    <button
                      onClick={() => setShowAttendees(!showAttendees)}
                      className="bg-gray-200 text-gray-800 py-2 px-6 rounded hover:bg-gray-300 transition duration-200"
                    >
                      {showAttendees ? 'Hide Attendee List' : 'View Attendees'}
                    </button>
                  )}
                </div>
              </div>

              {/* Admin Controls */}
              {isAdmin && (
                <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Controls</h3>
                  <div className="flex flex-wrap gap-4">
                    <button
                      onClick={() => handleExportAttendees(false)}
                      className="flex items-center bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition duration-200"
                    >
                      <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export Attendees (Email List)
                    </button>
                    <button
                      onClick={() => handleExportAttendees(true)}
                      className="flex items-center bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
                    >
                      <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export with Payment Info
                    </button>
                    <Link
                      to={`/admin/events/edit/${event.id}`}
                      className="flex items-center bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700 transition duration-200"
                    >
                      <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit Event
                    </Link>
                    <button
                      onClick={handleDeleteEvent}
                      className="flex items-center bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition duration-200"
                    >
                      <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Event
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Export attendee lists for email distribution. Payment info export includes transaction details for accounting. Use delete to permanently cancel events.
                  </p>
                </div>
              )}

              {/* Event Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Details</h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <svg className="h-6 w-6 text-apn-blue mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <p className="font-medium text-gray-900">Date</p>
                        <p className="text-gray-600">{event.date}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <svg className="h-6 w-6 text-apn-blue mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="font-medium text-gray-900">Time</p>
                        <p className="text-gray-600">
                          {(() => {
                            // First check for dedicated time field
                            if (event.time) {
                              return event.time;
                            }

                            // Try to extract time from date field
                            if (event.date && typeof event.date === 'string') {
                              // Handle "Wednesday, October 8, 2025 at 6:00 PM" format
                              if (event.date.includes(' at ')) {
                                return event.date.split(' at ')[1]?.trim();
                              }
                              // Handle other formats with "at"
                              if (event.date.includes('at')) {
                                return event.date.split('at')[1]?.trim();
                              }
                            }

                            // Fallback time
                            return '6:00 PM - 9:00 PM';
                          })()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <svg className="h-6 w-6 text-apn-blue mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div>
                        <p className="font-medium text-gray-900">Location</p>
                        <p className="text-gray-600">{event.location}</p>
                        {event.address && <p className="text-gray-600">{event.address}</p>}
                      </div>
                    </div>
                    <div className="flex items-start">
                      <svg className="h-6 w-6 text-apn-blue mr-3 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="font-medium text-gray-900">Price</p>
                        <p className="text-gray-600">{getPriceRange(event)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Add to Calendar</h3>
                  <div className="space-y-3">
                    <a
                      href={generateGoogleCalendarUrl(event)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-gray-700 hover:text-apn-blue transition duration-200"
                    >
                      <svg className="h-6 w-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm6.804 16.863h-1.275v-6.934H7.471v6.934H6.196v-8.59h12.608v8.59zm-8.141.001H9.387v-5.66h1.276v5.66zm2.276 0h-1.276v-5.66h1.276v5.66zm2.276 0h-1.276v-5.66h1.276v5.66z" />
                      </svg>
                      Add to Google Calendar
                    </a>
                    <a
                      href={generateOutlookCalendarUrl(event)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-gray-700 hover:text-apn-blue transition duration-200"
                    >
                      <svg className="h-6 w-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z" />
                      </svg>
                      Add to Outlook Calendar
                    </a>
                    <button
                      onClick={() => downloadICalendarFile(event)}
                      className="flex items-center text-gray-700 hover:text-apn-blue transition duration-200"
                    >
                      <svg className="h-6 w-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4V1h2v2h6V1h2v2zm-2 2H9v2H7V5H3v4h18V5h-4v2h-2V5zm5 6H4v8h16v-8z" />
                      </svg>
                      Download iCalendar File
                    </button>
                  </div>
                </div>
              </div>

              {/* Event Description */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">About This Event</h3>
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: event.longDescription }}
                />
              </div>

              {/* Registration Form - only show for upcoming events with ticket types/pricing */}
              {showRegistration && (event.ticketTypes || event.pricing) && !isEventPast(event) && (
                <div className="mb-8 p-6 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Registration</h3>

                  {/* Discount Code Section */}
                  <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Discount Code</h4>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        placeholder="Enter discount code (optional)"
                        value={discountCode}
                        onChange={(e) => handleDiscountCode(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                      />
                      {discountValid && (
                        <div className="flex items-center text-green-600">
                          <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          {discountAmount}% off applied!
                        </div>
                      )}
                    </div>
                    {/* Admin-only discount code information */}
                    {isAdmin && (
                      <div className="mt-2 text-sm text-gray-600">
                        <div className="flex justify-between items-center mb-2">
                          <p className="font-semibold text-red-600">Admin Only - Discount Codes:</p>
                          <button
                            onClick={() => setShowDiscountManager(true)}
                            className="bg-apn-blue text-white px-3 py-1 rounded text-xs hover:bg-blue-700"
                          >
                            Manage Codes
                          </button>
                        </div>

                        {/* Show event-specific codes if available */}
                        {event?.discountCodes && event.discountCodes.length > 0 ? (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Event-specific codes:</p>
                            <ul className="list-disc list-inside ml-2">
                              {event.discountCodes
                                .filter(code => code.active)
                                .map((code, index) => (
                                <li key={index}>
                                  <strong>{code.code}</strong> - {code.discount}
                                  {code.type === 'percentage' ? '%' : '$'} off
                                  {code.description && ` (${code.description})`}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Default codes (click Manage Codes to add event-specific ones):</p>
                            <ul className="list-disc list-inside ml-2">
                              <li><strong>ADMIN50</strong> - 50% admin discount</li>
                              <li><strong>SPONSOR25</strong> - 25% sponsor discount</li>
                              <li><strong>RELAUNCH20</strong> - 20% event special discount</li>
                            </ul>
                          </div>
                        )}

                        <p className="text-xs text-red-500 mt-1">
                          ⚠️ These codes are for admin distribution only and not visible to regular users.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Use new pricing structure if available, otherwise fall back to old ticketTypes */}
                  {event.pricing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Member Pricing */}
                        {event.pricing.member !== undefined && (
                          <div className="border border-gray-200 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900">Member Price</h4>
                            <div className="text-2xl font-bold text-apn-blue">
                              {discountValid ? (
                                <div>
                                  <span className="line-through text-gray-400 text-lg">
                                    ${event.pricing.member.toFixed(2)}
                                  </span>
                                  <div>${calculateDiscountedPrice(event.pricing.member).toFixed(2)}</div>
                                  <div className="text-sm text-green-600">({discountAmount}% off)</div>
                                </div>
                              ) : (
                                <span>${event.pricing.member.toFixed(2)}</span>
                              )}
                            </div>
                            <button
                              className="w-full mt-3 bg-apn-blue text-white py-2 px-4 rounded hover:bg-blue-700 transition duration-200 disabled:opacity-50"
                              onClick={() => handleStripeCheckout('member')}
                              disabled={paymentLoading || !currentUser}
                            >
                              {paymentLoading ? 'Processing...' : !currentUser ? 'Login Required' : 'Pay with Stripe'}
                            </button>
                            {!currentUser && (
                              <p className="text-xs text-red-600 mt-1">Please log in for member pricing</p>
                            )}
                          </div>
                        )}

                        {/* Non-Member Pricing */}
                        {event.pricing.nonMember !== undefined && (
                          <div className="border border-gray-200 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900">Non-Member Price</h4>
                            <div className="text-2xl font-bold text-gray-600">
                              {discountValid ? (
                                <div>
                                  <span className="line-through text-gray-400 text-lg">
                                    ${event.pricing.nonMember.toFixed(2)}
                                  </span>
                                  <div>${calculateDiscountedPrice(event.pricing.nonMember).toFixed(2)}</div>
                                  <div className="text-sm text-green-600">({discountAmount}% off)</div>
                                </div>
                              ) : (
                                <span>${event.pricing.nonMember.toFixed(2)}</span>
                              )}
                            </div>
                            <button
                              className="w-full mt-3 bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700 transition duration-200 disabled:opacity-50"
                              onClick={() => handleStripeCheckout('nonMember')}
                              disabled={paymentLoading}
                            >
                              {paymentLoading ? 'Processing...' : 'Pay with Stripe'}
                            </button>
                            <p className="text-xs text-green-600 mt-1">✓ No login required</p>
                          </div>
                        )}

                        {/* Sponsor Access - Discount Code Only */}
                        <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                          <h4 className="font-semibold text-gray-900 mb-2">Sponsor Access</h4>
                          <p className="text-sm text-gray-600 mb-3">
                            Sponsors receive special discount codes. Enter your assigned code below to access sponsor pricing.
                          </p>
                          <div className="space-y-2">
                            <label htmlFor="sponsorCode" className="block text-sm font-medium text-gray-700">
                              Sponsor Discount Code
                            </label>
                            <input
                              type="text"
                              id="sponsorCode"
                              value={discountCode}
                              onChange={(e) => handleDiscountCode(e.target.value)}
                              placeholder="Enter sponsor code..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
                            />
                            {discountValid && (
                              <p className="text-sm text-green-600">
                                ✅ Valid code! {discountAmount}% discount applied
                              </p>
                            )}
                            {discountCode && !discountValid && (
                              <p className="text-sm text-red-600">
                                ❌ Invalid discount code
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Secure Payment:</strong> Click "Pay with Stripe" to proceed to secure checkout.
                          You'll be redirected to Stripe's secure payment page to complete your registration.
                        </p>
                        <div className="mt-2 text-sm">
                          <p className="text-green-600">
                            ✓ <strong>Non-members:</strong> Can register without login
                          </p>
                          <p className="text-blue-600">
                            ℹ <strong>Members:</strong> Please log in for member pricing
                          </p>
                          <p className="text-purple-600">
                            🎫 <strong>Sponsors:</strong> Use your assigned discount code for special pricing
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <EventRegistration
                      event={event}
                      isLoggedIn={isUserLoggedIn}
                    />
                  )}
                </div>
              )}

              {/* Show message for past events */}
              {showRegistration && isEventPast(event) && (
                <div className="mb-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">Registration Closed</h3>
                  <p className="text-yellow-700">
                    This event has already taken place. Registration is no longer available.
                  </p>
                </div>
              )}

              {/* Connected Attendees - Show for logged in members */}
              {isUserLoggedIn && (
                <div className="mb-8">
                  <ConnectedAttendees event={event} />
                </div>
              )}

              {/* Attendee List */}
              {showAttendees && isUserLoggedIn && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Attendees</h3>
                  <AttendeeList event={event} />
                </div>
              )}


            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Ready to Join the Network?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Connect with finance professionals, access exclusive resources, and accelerate your career with APN.
          </p>
          <Link to="/register" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Become a Member Today
          </Link>
        </div>
      </section>

      {/* Admin Discount Manager Modal */}
      {showDiscountManager && (
        <EventDiscountManager
          eventId={event?.id}
          onClose={() => setShowDiscountManager(false)}
        />
      )}
    </div>
  );
};

export default EventDetail;
